import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { useOperations } from './OperationsProvider';
import {
  areaTypeLabel, humanMs, isStale, waitingMs,
} from './areas';
import { driverContact } from './drivers';
import DownloadTable from '../shell/DownloadTable';
import { attentionOf, worstOf, attentionText } from './attention';
import AreaBoard from './AreaBoard';
import { formatDateTime } from '../common/util/datetime';

// Who is standing inside which geofence, as a list.
//
// The map answers this already, but only for as many pins as fit on a screen and only one glance at
// a time. The questions people actually ask are list questions - how many are in the plant, who has
// been in parking longest, which of them is not reporting - and a map answers none of them without
// counting pins by hand.
//
// Nothing here is new data. Every area already carries its occupants, when each entered, and whether
// that vehicle's device has gone quiet; this is that, arranged so it can be sorted, filtered and
// taken away.
//
// A vehicle standing in two overlapping areas appears once per area, because that is the truth and
// collapsing it would hide an overlap somebody probably wants to know about.

const AreaOccupancyPage = () => {
  const navigate = useNavigate();
  const { data, trips, loading } = useOperations();
  const [area, setArea] = useState('ALL');
  const [kind, setKind] = useState('ALL');
  // With forty lorries on site the three that matter should not need finding.
  const [onlyAttention, setOnlyAttention] = useState(false);
  // How the yard is laid out, against the list that sorts and downloads. The board answers the
  // question people arrive with - how are the lorries spread across my site - which rows answer only
  // by being counted.
  const [view, setView] = useState('BOARD');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const areas = data?.areas || [];
  const vehicles = data?.vehicles || [];

  const rows = useMemo(() => {
    const byVehicle = new Map(vehicles.map((v) => [v.id, v]));
    // An open trip tells us why the vehicle is there, which is usually the next question.
    const openTrip = new Map();
    (trips || []).forEach((t) => { if (!t.closedAt) openTrip.set(t.vehicleId, t); });

    const out = [];
    areas.forEach((a) => (a.occupants || []).forEach((occupant) => {
      const vehicle = byVehicle.get(occupant.vehicleId);
      const trip = openTrip.get(occupant.vehicleId) || null;
      out.push({
        key: `${a.id}-${occupant.vehicleId}`,
        areaId: a.id,
        area: a.name,
        areaType: a.type,
        vehicleId: occupant.vehicleId,
        vehicle: vehicle?.name || `Vehicle ${occupant.vehicleId}`,
        transporter: trip?.transporter || vehicle?.transporter || '',
        enteredAt: occupant.enteredAt || null,
        insideMs: waitingMs(a, occupant.vehicleId),
        stale: isStale(a, occupant.vehicleId),
        driver: driverContact(data?.drivers, trip || vehicle),
        byReader: !!occupant.byReader,
        trip,
      });
    }));
    // Judged before anything is filtered out, so the summary above counts what is actually on site
    // rather than what happens to be on screen.
    out.forEach((r) => {
      r.attention = attentionOf(
        {
          areaType: r.areaType, insideMs: r.insideMs, stale: r.stale, trip: r.trip, byReader: r.byReader,
        },
        data?.config,
      );
    });

    const needle = search.trim().toLowerCase();
    return out
      .filter((r) => (area === 'ALL' || String(r.areaId) === String(area)))
      .filter((r) => (kind === 'ALL' || r.areaType === kind))
      .filter((r) => !needle || `${r.vehicle} ${r.area} ${r.transporter} ${r.driver.name}`.toLowerCase().includes(needle))
      .filter((r) => !onlyAttention || !!r.attention)
      // Longest inside first: the vehicle that has been standing the longest is the one somebody
      // needs to do something about.
      .sort((x, y) => (y.insideMs ?? -1) - (x.insideMs ?? -1));
  }, [areas, vehicles, trips, data, area, kind, search, onlyAttention]);

  // One card per area: how many are in it, how long the longest has been there, and whether any of
  // them is a problem. Counted from every occupant of that area, not from the filtered rows, so
  // narrowing the list never quietly changes the totals above it.
  const summary = useMemo(() => {
    const byVehicle = new Map(vehicles.map((v) => [v.id, v]));
    const openTrip = new Map();
    (trips || []).forEach((t) => { if (!t.closedAt) openTrip.set(t.vehicleId, t); });
    return areas.map((a) => {
      const occupants = (a.occupants || []).map((o) => ({
        areaType: a.type,
        insideMs: waitingMs(a, o.vehicleId),
        stale: isStale(a, o.vehicleId),
        trip: openTrip.get(o.vehicleId) || null,
        vehicle: byVehicle.get(o.vehicleId),
      }));
      const judged = occupants.map((o) => ({ attention: attentionOf(o, data?.config) }));
      return {
        id: a.id,
        name: a.name,
        type: a.type,
        inside: occupants.length,
        longestMs: occupants.reduce((held, o) => Math.max(held, o.insideMs ?? 0), 0),
        needing: judged.filter((o) => o.attention).length,
        worst: worstOf(judged),
      };
    });
  }, [areas, vehicles, trips, data]);

  const needingTotal = useMemo(
    () => summary.reduce((sum, a) => sum + a.needing, 0),
    [summary],
  );

  const columns = [
    ['Vehicle', (r) => r.vehicle],
    ['Area', (r) => r.area],
    ['Area type', (r) => areaTypeLabel(r.areaType)],
    ['Entered at', (r) => (r.enteredAt ? formatDateTime(r.enteredAt) : '')],
    ['Time inside', (r) => (r.insideMs == null ? '' : humanMs(r.insideMs))],
    ['Last seen by', (r) => (r.byReader ? 'Reader' : r.stale ? 'Tracker (gone quiet)' : 'Tracker')],
    ['Needs attention', (r) => (r.attention ? r.attention.label : '')],
    ['Driver', (r) => r.driver.name],
    ['Driver mobile', (r) => r.driver.mobile],
    ['Transporter', (r) => r.transporter],
    ['Trip', (r) => (r.trip ? (r.trip.shipmentId || `Q-${r.trip.id}`) : '')],
  ];

  const shown = rows.slice(page * pageSize, page * pageSize + pageSize);
  const inside = new Set(rows.map((r) => r.vehicleId)).size;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight={700}>Area occupancy</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {loading && !areas.length
          ? 'Loading areas…'
          : `${inside} vehicle${inside === 1 ? '' : 's'} standing inside ${areas.length} geofenced area${areas.length === 1 ? '' : 's'}, longest first.`}
      </Typography>

      {!areas.length && !loading && (
        <Alert severity="info">
          No geofenced areas are defined yet. Draw one under Configuration to see who is standing in it.
        </Alert>
      )}

      {!!areas.length && (
        <>
          {/* The glance. Each card is also the filter for its own area, because the thing somebody
              wants to narrow to is the thing they have just read. */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(auto-fit, minmax(230px, 1fr))' }, gap: 1.5, mb: 2 }}>
            {summary.map((a) => {
              const chosen = String(area) === String(a.id);
              return (
                <Paper
                  key={a.id}
                  variant="outlined"
                  onClick={() => { setArea(chosen ? 'ALL' : String(a.id)); setKind('ALL'); setPage(0); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setArea(chosen ? 'ALL' : String(a.id)); setKind('ALL'); setPage(0); } }}
                  role="button"
                  tabIndex={0}
                  sx={{
                    p: 1.75,
                    cursor: 'pointer',
                    borderWidth: chosen ? 2 : 1,
                    borderColor: chosen ? 'primary.main' : undefined,
                    // The worst news in the area, down its leading edge, so a wall of cards can be
                    // read without reading any of them.
                    borderLeft: 6,
                    borderLeftColor: a.worst === 'error' ? 'error.main' : a.worst === 'warning' ? 'warning.main' : 'success.main',
                  }}
                >
                  <Typography variant="body2" fontWeight={700} noWrap title={a.name}>{a.name}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{areaTypeLabel(a.type)}</Typography>
                  <Stack direction="row" alignItems="baseline" gap={1} sx={{ mt: 0.75 }}>
                    <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>{a.inside}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {a.inside === 1 ? 'vehicle inside' : 'vehicles inside'}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {a.inside ? `Longest ${humanMs(a.longestMs)}` : 'Empty'}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    color={a.needing ? (a.worst === 'error' ? 'error.main' : 'warning.main') : 'success.main'}
                    fontWeight={a.needing ? 700 : 400}
                  >
                    {attentionText(a.needing)}
                  </Typography>
                </Paper>
              );
            })}
          </Box>

          <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mb: 2 }}>
            <Stack direction="row" sx={{ height: 40 }}>
              <Button
                size="small"
                variant={view === 'BOARD' ? 'contained' : 'outlined'}
                sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                onClick={() => setView('BOARD')}
              >
                Board
              </Button>
              <Button
                size="small"
                variant={view === 'LIST' ? 'contained' : 'outlined'}
                sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, ml: '-1px' }}
                onClick={() => setView('LIST')}
              >
                List
              </Button>
            </Stack>
            <TextField
              size="small"
              label="Find a vehicle, driver or carrier"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              inputProps={{ maxLength: 60 }}
              sx={{ minWidth: 260 }}
            />
            <TextField size="small" select label="Area" value={area} onChange={(e) => { setArea(e.target.value); setPage(0); }} sx={{ minWidth: 190 }}>
              <MenuItem value="ALL">All areas</MenuItem>
              {areas.map((a) => <MenuItem key={a.id} value={String(a.id)}>{a.name}</MenuItem>)}
            </TextField>
            <TextField size="small" select label="Type" value={kind} onChange={(e) => { setKind(e.target.value); setPage(0); }} sx={{ minWidth: 170 }}>
              <MenuItem value="ALL">All types</MenuItem>
              <MenuItem value="PLANT">{areaTypeLabel('PLANT')}</MenuItem>
              <MenuItem value="PARKING">{areaTypeLabel('PARKING')}</MenuItem>
              <MenuItem value="WAREHOUSE">{areaTypeLabel('WAREHOUSE')}</MenuItem>
            </TextField>
            <Tooltip title={needingTotal ? 'Show only the vehicles something has to be done about' : 'Nothing needs attention right now'}>
              <span>
                <Button
                  size="small"
                  sx={{ height: 40 }}
                  variant={onlyAttention ? 'contained' : 'outlined'}
                  color={needingTotal ? 'error' : 'inherit'}
                  disabled={!needingTotal && !onlyAttention}
                  onClick={() => { setOnlyAttention((on) => !on); setPage(0); }}
                >
                  {onlyAttention ? 'Show all' : attentionText(needingTotal)}
                </Button>
              </span>
            </Tooltip>
            <Box sx={{ flex: 1 }} />
            <DownloadTable name="area-occupancy" title="Area occupancy" rows={rows} columns={columns} />
          </Stack>

          {!rows.length && (
            <Alert severity="info">Nothing matches that filter. No vehicle is standing in those areas right now.</Alert>
          )}

          {!!rows.length && view === 'BOARD' && (
            <AreaBoard
              areas={areas}
              rows={rows}
              onOpen={(tripId) => navigate(`/trips/${tripId}`)}
            />
          )}

          {!!rows.length && view === 'LIST' && (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Vehicle', 'Area', 'Entered at', 'Time inside', 'Driver', 'Trip'].map((c) => (
                        <TableCell key={c}>{c}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shown.map((r) => (
                      <TableRow
                        key={r.key}
                        hover
                        tabIndex={0}
                        sx={{
                          cursor: r.trip ? 'pointer' : 'default',
                          // The same leading edge as the cards, so the eye carries down the page.
                          borderLeft: 4,
                          borderLeftColor: r.attention
                            ? (r.attention.level === 'error' ? 'error.main' : 'warning.main')
                            : 'transparent',
                        }}
                        onClick={() => r.trip && navigate(`/trips/${r.trip.id}`)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && r.trip) navigate(`/trips/${r.trip.id}`); }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{r.vehicle}</Typography>
                          {!!r.transporter && (
                            <Typography variant="caption" color="text.secondary">{r.transporter}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {r.area}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {areaTypeLabel(r.areaType)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {r.enteredAt ? formatDateTime(r.enteredAt) : 'Not recorded'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                          {r.insideMs == null ? '—' : humanMs(r.insideMs)}
                          {/* Why this row is marked, in the words the exceptions list uses - a
                              device gone quiet, a lorry on site with nothing open against it, or
                              one waited past the plant's own call-in time. */}
                          {r.attention && (
                            <Tooltip title={r.attention.detail}>
                              <Chip
                                size="small"
                                label={r.attention.label}
                                color={r.attention.level === 'error' ? 'error' : 'warning'}
                                variant="outlined"
                                sx={{ ml: 1 }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          {r.driver.name || '—'}
                          {!!r.driver.mobile && (
                            <Typography variant="caption" color="text.secondary" display="block">{r.driver.mobile}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {r.trip
                            ? (r.trip.shipmentId || `Q-${r.trip.id}`)
                            : <Typography variant="body2" color="text.secondary">—</Typography>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={rows.length}
                page={page}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[25, 50, 100]}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default AreaOccupancyPage;
